/**
 * Expression Evaluator
 *
 * Evaluates JavaScript expressions within ${...} syntax.
 * Uses a sandboxed environment with whitelisted globals and workflow context functions.
 *
 * Available workflow functions:
 * - trigger() - Get the trigger output data
 * - triggerBody() - Get the trigger body
 * - triggerOutputs() - Get the trigger outputs
 * - body('actionName') - Get the body of an action's output
 * - outputs('actionName') - Get an action's outputs
 * - actions('actionName') - Get an action's full result (outputs + status)
 * - variables('name') - Get a workflow variable
 * - parameters('name') - Get a workflow parameter
 * - items('scopeName') - Get the current item from a ForEach loop (runtime only)
 * - item() - Get the current item from the innermost ForEach loop (runtime only)
 * - iterationIndexes('scopeName') - Get the current iteration index (runtime only)
 *
 * Available libraries:
 * - DateTime, Duration, Interval (Luxon) - Date/time manipulation
 * - jmespath(data, query) - Query JSON using JMESPath expressions
 *
 * Available utilities:
 * - uuid() - Generate a random UUID
 * - console.log/warn/error - Debug output
 *
 * Users can use standard JavaScript for all other operations.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import type { EvaluationContext, EvaluationResult } from './types';
import { DateTime, Duration, Interval, Settings } from 'luxon';
import { search as jmespathSearch, compile as jmespathCompile } from '@metrichor/jmespath';

// Configure Luxon to throw on invalid dates (makes debugging easier)
Settings.throwOnInvalid = true;

// =============================================================================
// SANDBOX CREATION
// =============================================================================

function createSandbox(context: EvaluationContext): (code: string) => any {
  const triggerData = context.trigger ?? {};
  const actionOutputs = context.actionOutputs ?? {};

  // Workflow context functions
  const workflowHelpers = {
    trigger: () => triggerData,
    triggerBody: () => {
      const data = triggerData as any;
      return data?.body ?? data;
    },
    triggerOutputs: () => triggerData,
    body: (actionName: string) => {
      const output = actionOutputs[actionName];
      if (output && typeof output === 'object') {
        return (output as any).body ?? output;
      }
      return output;
    },
    outputs: (actionName: string) => actionOutputs[actionName],
    actions: (actionName: string) => ({
      outputs: actionOutputs[actionName],
      status: actionOutputs[actionName] !== undefined ? 'Succeeded' : 'Unknown',
    }),
    variables: (name: string) => context.variables?.[name],
    parameters: (name: string) => context.parameters?.[name],
    // Loop functions — return undefined at design time (resolved at runtime)
    items: (scopeName: string) => context.loopItems?.[scopeName],
    item: () => undefined,
    iterationIndexes: (_scopeName: string) => undefined,
  };

  // Safe console that only allows logging (no-op functions that don't leak info)
  const safeConsole = {
    log: (...args: any[]) => console.log('[Expression]', ...args),
    warn: (...args: any[]) => console.warn('[Expression]', ...args),
    error: (...args: any[]) => console.error('[Expression]', ...args),
    info: (...args: any[]) => console.info('[Expression]', ...args),
    debug: (...args: any[]) => console.debug('[Expression]', ...args),
  };

  // UUID generator
  const uuid = () => {
    // Use crypto.randomUUID if available, otherwise fallback
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback UUID v4 generation
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  // Allowed globals - standard JavaScript built-ins
  const allowedGlobals: Record<string, any> = {
    // Built-in objects
    Math,
    JSON,
    Date,
    Number,
    String,
    Array,
    Object,
    Boolean,
    RegExp,
    Map,
    Set,
    Promise,

    // Safe functions
    parseInt,
    parseFloat,
    isNaN,
    isFinite,
    encodeURI,
    decodeURI,
    encodeURIComponent,
    decodeURIComponent,
    btoa,
    atob,

    // Global values
    NaN,
    Infinity,
    undefined,

    // Luxon date/time library (safe - pure functions, no side effects)
    DateTime,
    Duration,
    Interval,

    // JMESPath for JSON querying (safe - pure query language, no code execution)
    jmespath: jmespathSearch,
    jmespathCompile,

    // Utilities
    uuid,
    console: safeConsole,

    // Workflow context functions
    ...workflowHelpers,
  };

  return (code: string): any => {
    const paramNames = Object.keys(allowedGlobals);
    const paramValues = Object.values(allowedGlobals);

    // Create and execute the function
    const fn = new Function(...paramNames, `return (${code});`);
    return fn(...paramValues);
  };
}

// =============================================================================
// MAIN EVALUATION FUNCTIONS
// =============================================================================

/**
 * Evaluate a single expression (without ${...} wrapper)
 */
export function evaluateExpression(
  expression: string,
  context: EvaluationContext = {}
): EvaluationResult {
  // Remove ${...} wrapper if present
  let code = expression.trim();
  if (code.startsWith('${') && code.endsWith('}')) {
    code = code.slice(2, -1).trim();
  } else if (code.startsWith('@{') && code.endsWith('}')) {
    code = code.slice(2, -1).trim();
  } else if (code.startsWith('@') && !code.startsWith('@{')) {
    // Handle inline @function(...) format like @variables('name'), @body('action')
    code = code.slice(1).trim();
  }

  if (!code) {
    return {
      success: false,
      error: 'Empty expression',
      expression,
    };
  }

  try {
    const sandbox = createSandbox(context);
    const result = sandbox(code);

    return {
      success: true,
      value: result,
      expression,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      expression,
    };
  }
}

/**
 * Evaluate a template string with multiple ${...}, @{...}, or @function() expressions
 */
export function evaluateTemplate(
  template: string,
  context: EvaluationContext = {}
): EvaluationResult {
  if (!template) {
    return {
      success: true,
      value: '',
      expression: template,
    };
  }

  try {
    let result = '';
    let pos = 0;
    let hasExpression = false;
    let lastExpressionResult: unknown = undefined;

    while (pos < template.length) {
      // Check for escaped $$
      if (pos < template.length - 1 && template.charAt(pos) === '$' && template.charAt(pos + 1) === '$') {
        result += '$';
        pos += 2;
        continue;
      }

      // Check for expression start ${
      if (pos < template.length - 1 && template.charAt(pos) === '$' && template.charAt(pos + 1) === '{') {
        const end = findExpressionEnd(template, pos);
        if (end > 0) {
          const expr = template.substring(pos, end);
          const evalResult = evaluateExpression(expr, context);

          if (!evalResult.success) {
            return {
              success: false,
              error: evalResult.error ?? 'Unknown error',
              expression: template,
            };
          }

          hasExpression = true;
          lastExpressionResult = evalResult.value;

          // Convert to string for template interpolation
          if (evalResult.value === undefined) {
            result += 'undefined';
          } else if (evalResult.value === null) {
            result += 'null';
          } else if (typeof evalResult.value === 'object') {
            result += JSON.stringify(evalResult.value);
          } else {
            result += String(evalResult.value);
          }

          pos = end;
          continue;
        }
      }

      // Check for Logic Apps @{...} expression format
      if (pos < template.length - 1 && template.charAt(pos) === '@' && template.charAt(pos + 1) === '{') {
        const end = findAtBraceExpressionEnd(template, pos);
        if (end > 0) {
          const expr = template.substring(pos, end);
          const evalResult = evaluateExpression(expr, context);

          if (!evalResult.success) {
            return {
              success: false,
              error: evalResult.error ?? 'Unknown error',
              expression: template,
            };
          }

          hasExpression = true;
          lastExpressionResult = evalResult.value;

          if (evalResult.value === undefined) {
            result += 'undefined';
          } else if (evalResult.value === null) {
            result += 'null';
          } else if (typeof evalResult.value === 'object') {
            result += JSON.stringify(evalResult.value);
          } else {
            result += String(evalResult.value);
          }

          pos = end;
          continue;
        }
      }

      // Check for Logic Apps inline @function(...) expression format
      if (template.charAt(pos) === '@' && pos + 1 < template.length && /[a-zA-Z]/.test(template.charAt(pos + 1))) {
        const end = findInlineAtExpressionEnd(template, pos);
        if (end > pos) {
          const expr = template.substring(pos, end);
          const evalResult = evaluateExpression(expr, context);

          if (!evalResult.success) {
            return {
              success: false,
              error: evalResult.error ?? 'Unknown error',
              expression: template,
            };
          }

          hasExpression = true;
          lastExpressionResult = evalResult.value;

          if (evalResult.value === undefined) {
            result += 'undefined';
          } else if (evalResult.value === null) {
            result += 'null';
          } else if (typeof evalResult.value === 'object') {
            result += JSON.stringify(evalResult.value);
          } else {
            result += String(evalResult.value);
          }

          pos = end;
          continue;
        }
      }

      result += template.charAt(pos);
      pos++;
    }

    // If the template is purely a single expression, return the actual value type
    const trimmed = template.trim();
    const isSingleDollarExpr = trimmed.startsWith('${') && trimmed.endsWith('}');
    const isSingleAtBraceExpr = trimmed.startsWith('@{') && trimmed.endsWith('}');
    const isSingleInlineExpr = trimmed.startsWith('@') && !trimmed.startsWith('@{') && /^@[a-zA-Z_]+\(/.test(trimmed);

    if (hasExpression && (isSingleDollarExpr || isSingleAtBraceExpr || isSingleInlineExpr)) {
      return {
        success: true,
        value: lastExpressionResult,
        expression: template,
      };
    }

    return {
      success: true,
      value: result,
      expression: template,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      expression: template,
    };
  }
}

/**
 * Find the end of a @{...} expression (handling nested braces and strings)
 */
function findAtBraceExpressionEnd(text: string, start: number): number {
  if (start + 2 >= text.length) return -1;
  if (text.charAt(start) !== '@' || text.charAt(start + 1) !== '{') return -1;

  let pos = start + 2;
  let braceDepth = 1;
  let inString = false;
  let stringChar = '';

  while (pos < text.length && braceDepth > 0) {
    const char = text.charAt(pos);

    if (inString) {
      if (char === stringChar) {
        let backslashCount = 0;
        let k = pos - 1;
        while (k >= 0 && text.charAt(k) === '\\') {
          backslashCount++;
          k--;
        }
        if (backslashCount % 2 === 0) {
          inString = false;
        }
      }
    } else {
      if (char === '"' || char === "'") {
        inString = true;
        stringChar = char;
      } else if (char === '{') {
        braceDepth++;
      } else if (char === '}') {
        braceDepth--;
      }
    }
    pos++;
  }

  return braceDepth === 0 ? pos : -1;
}

/**
 * Find the end of an inline @function(...) expression
 */
function findInlineAtExpressionEnd(text: string, start: number): number {
  if (text.charAt(start) !== '@') return -1;

  let pos = start + 1;

  while (pos < text.length && /[a-zA-Z_]/.test(text.charAt(pos))) {
    pos++;
  }

  if (pos >= text.length || text.charAt(pos) !== '(') {
    return -1;
  }

  let depth = 1;
  pos++;
  let inString = false;
  let stringChar = '';

  while (pos < text.length && depth > 0) {
    const char = text.charAt(pos);

    if (inString) {
      if (char === stringChar) {
        let backslashCount = 0;
        let k = pos - 1;
        while (k >= 0 && text.charAt(k) === '\\') {
          backslashCount++;
          k--;
        }
        if (backslashCount % 2 === 0) {
          inString = false;
        }
      }
    } else {
      if (char === '"' || char === "'") {
        inString = true;
        stringChar = char;
      } else if (char === '(') {
        depth++;
      } else if (char === ')') {
        depth--;
      }
    }
    pos++;
  }

  return depth === 0 ? pos : -1;
}

/**
 * Find the end of a ${...} expression (handling nested braces and strings)
 */
function findExpressionEnd(text: string, start: number): number {
  if (start + 2 >= text.length) return -1;
  if (text.charAt(start) !== '$' || text.charAt(start + 1) !== '{') return -1;

  let pos = start + 2;
  let braceDepth = 0;
  let inString = false;
  let stringChar = '';
  let escaped = false;

  while (pos < text.length) {
    const char = text.charAt(pos);

    if (escaped) {
      escaped = false;
      pos++;
      continue;
    }

    if (char === '\\') {
      escaped = true;
      pos++;
      continue;
    }

    if (!inString) {
      if (char === '"' || char === "'" || char === '`') {
        inString = true;
        stringChar = char;
        pos++;
        continue;
      }

      if (char === '{') {
        braceDepth++;
        pos++;
        continue;
      }

      if (char === '}') {
        if (braceDepth === 0) {
          return pos + 1;
        }
        braceDepth--;
        pos++;
        continue;
      }
    } else {
      if (char === stringChar) {
        inString = false;
        stringChar = '';
      }
    }

    pos++;
  }

  return -1;
}

/** Matches loop function calls: items(), item(), iterationIndexes() */
export const LOOP_FUNCTION_PATTERN = /\bitems\s*\(|\biterationIndexes\s*\(|\bitem\s*\(/;

/**
 * Check if an expression can be evaluated (has all required data)
 */
export function canEvaluate(expression: string, context: EvaluationContext): boolean {
  const referencesActions = /body\s*\(|outputs\s*\(|actions\s*\(/.test(expression);
  const referencesTrigger = /trigger(?:Body|Outputs)?\s*\(/.test(expression);
  const referencesLoop = LOOP_FUNCTION_PATTERN.test(expression);

  if (referencesActions && !context.actionOutputs) {
    return false;
  }

  if (referencesTrigger && !context.trigger) {
    return false;
  }

  if (referencesLoop && !context.loopItems) {
    return false;
  }

  return true;
}
